import { Injectable, NgZone } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { v4 as uuidv4 } from 'uuid';
import { CommanSubscribeService } from 'src/app/core/services/comman-subscribe.service';
import { PubNubService } from 'src/app/core/services/pubnub.service';
import { commentsEnabledFor } from '../video-detail-panel';
import { addCommentItem, albumCommentsChannel, commentAuthorName, rememberWatchedAlbum, toCommentItem, wasAlbumWatched } from '../video-detail-comments';

/*
 * The album's comments before anything plays, for fans who already watched it on this browser (US-14).
 * Same channel, history, live updates and message as the player's comments; once a video plays the
 * player's comments are shown instead. Provided per page.
 */
@Injectable()
export class VdCommentsFeedService {
  comments: any[] = [];
  /** The Comments tab can show before playing. */
  active = false;
  private channel = '';
  private subscribed = '';

  constructor(private pubnub: PubNubService, private commonSubscribe: CommanSubscribeService,
    private toastr: ToastrService, private zone: NgZone) {}

  /** Playback started: remember the album, so next time its comments show straight away. */
  remember(albumId: any): void {
    rememberWatchedAlbum(albumId);
  }

  /** Album loaded: open its comments when the fan watched it before, comments are on and it isn't a preview. */
  load(albumId: any, firstVideo: any, isPreview: boolean): void {
    const channel = albumCommentsChannel(firstVideo);
    this.active = !isPreview && !!channel && commentsEnabledFor(firstVideo) && wasAlbumWatched(albumId);
    if (!this.active) return;
    this.channel = channel;
    this.comments = [];
    this.pubnub.init();
    this.pubnub.getAllMessage(channel, 100, messages => this.zone.run(() => {
      if (this.channel !== channel) return;
      this.comments = (messages || []).filter(m => m?.entry).map(m => toCommentItem(m.entry)).reverse();
    }));
    if (this.subscribed === channel) return;
    this.subscribed = channel;
    this.pubnub.receiveCommentMessages(channel, msg => this.zone.run(() => {
      if (msg?.message && this.channel === channel) this.comments = addCommentItem(this.comments, msg.message);
    }));
  }

  get signedIn(): boolean {
    return !!this.userInfo?.access_token;
  }

  /** Same message as the player's postComment; shown once PubNub accepts it. */
  post(text: string): void {
    const user = this.userInfo;
    if (!this.channel || !text.trim() || !user?.id) return;
    const payload = {
      type: 'text', message: text, customType: 'COMMENTS', userType: 'fan', messageId: uuidv4(), timestamp: Date.now(),
      _sender: { isSender: true, profileUrl: user.profile_pic || '', userId: 'fan_' + user.id, nickname: commentAuthorName(user) },
      publishAt: 'fan_' + user.id,
    };
    this.pubnub.sendMessage(this.channel, payload, status => this.zone.run(() => {
      if (status?.error) {
        this.toastr.error('Error while sending message');
        return;
      }
      this.comments = addCommentItem(this.comments, payload);
    }));
  }

  private get userInfo(): any {
    return this.commonSubscribe.userInfo;
  }
}
